"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseNumberFieldLogic = void 0;
const mean_1 = __importDefault(require("lodash-es/mean"));
const number_1 = require("../../aggregations/number");
const field_logic_1 = require("../../field-type-registry/field-logic");
const comparisons_1 = require("../../comparisons/comparisons");
const number_2 = require("../../helpers/number");
const texts_1 = require("../../texts");
const number_3 = require("../../utils/number");
const validation_1 = require("../../validation/validation");
const intl_1 = require("../../locale/intl");
const number_fill_pattern_1 = require("../../helpers/fill-patterns/number-fill-pattern");
const math_1 = require("../../utils/math");
class BaseNumberFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.aggregates = {
            empty: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.empty),
                aggregator: field_logic_1.countEmpty,
            },
            "non-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.non_empty),
                aggregator: field_logic_1.countFilled,
            },
            "percent-filled": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_filled),
                aggregator: field_logic_1.percentFilled,
            },
            "percent-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_empty),
                aggregator: field_logic_1.percentEmpty,
            },
            sum: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.sum),
                aggregator: (0, number_1.numbersAggregate)((values) => values.reduce((acc, value) => acc + value, 0)),
            },
            lowest: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.lowest),
                aggregator: (0, number_1.numbersAggregate)((values) => values.length ? Math.min(...values) : null),
            },
            highest: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.highest),
                aggregator: (0, number_1.numbersAggregate)((values) => values.length ? Math.max(...values) : null),
            },
            average: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.average),
                aggregator: (0, number_1.numbersAggregate)((values) => values.length ? (0, mean_1.default)(values) : null),
            },
            median: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.median),
                aggregator: (0, number_1.numbersAggregate)((values) => values.length ? (0, math_1.median)(values) : null),
            },
            std: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.standard_deviation),
                aggregator: (0, number_1.numbersAggregate)((values) => values.length ? (0, math_1.std)(values) : null),
            },
            range: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.range),
                aggregator: number_1.numberRangeAggregator,
            },
        };
        this.comparisons = {
            is_equal_to: comparisons_1.isEqualTo,
            is_not_equal_to: comparisons_1.isNotEqualTo,
            // @ts-expect-error: Need to resolve
            is_greater_than: comparisons_1.isGreaterThanAndNotEmpty,
            // @ts-expect-error: Need to resolve
            is_less_than: comparisons_1.isLessThanAndNotEmpty,
            // @ts-expect-error: Need to resolve
            is_equal_or_greater_than: comparisons_1.isEqualOrGreaterThanAndNotEmpty,
            // @ts-expect-error: Need to resolve
            is_equal_or_less_than: comparisons_1.isEqualOrLessThanAndNotEmpty,
            // @ts-expect-error: Need to resolve
            is_empty: comparisons_1.isEmpty,
            // @ts-expect-error: Need to resolve
            is_not_empty: comparisons_1.isNotEmpty,
        };
    }
    get validators() {
        const uniqueValidator = (0, validation_1.uniqueRecordFieldValueWithContext)({
            checkUniqueness: this.appRecordsService.checkUniqueness.bind(this.appRecordsService),
        });
        return { required: validation_1.requiredNumber, numberField: validation_1.numberField, unique: uniqueValidator };
    }
    processFilterValue(filterValue, _comparison, _field, _userPreferences) {
        return filterValue && +filterValue;
    }
    processFieldValue(record, slug, comparison, field) {
        const fieldValue = super.processFieldValue(record, slug, comparison, field);
        return fieldValue && +fieldValue;
    }
    processFieldValueForFiltering(record, slug, comparison, field) {
        var _a;
        const fieldValue = super.processFieldValueForFiltering(record, slug, comparison, field);
        return (fieldValue && (0, number_2.toFixed)(Number(fieldValue), (_a = field.params.precision) !== null && _a !== void 0 ? _a : 0));
    }
    valueToString(fieldValue, _field, _userPreferences) {
        if (fieldValue === null || fieldValue === undefined) {
            return "";
        }
        return `${fieldValue}`;
    }
    sortKey(fieldValue, field) {
        var _a;
        if (fieldValue === null || fieldValue === undefined) {
            return Number.NEGATIVE_INFINITY;
        }
        return (0, number_2.toFixed)(fieldValue, (_a = field.params.precision) !== null && _a !== void 0 ? _a : 0);
    }
    useNaturalSorting(field, fieldValue, isGroupByRule) {
        return false;
    }
    searchKey(fieldValue, field, userPreferences) {
        return this.valueToString(fieldValue, field, userPreferences);
    }
    prepareValueForClipboard(fieldValue, srcField, userPreferences) {
        return {
            text: this.valueToString(fieldValue, srcField, userPreferences),
            rawValue: fieldValue,
        };
    }
    validateValue(field, value, _partial = false, userPreferences) {
        // This is a temporary fix to allow free editing of the specified fields.
        const precision = [
            "currencyfield",
            "percentfield",
            "numberfield",
            "rollupfield",
            "formulafield",
        ].includes(field === null || field === void 0 ? void 0 : field.field_type)
            ? 6
            : field === null || field === void 0 ? void 0 : field.params.precision;
        const regex = (0, number_3.getNumberRegex)(precision, field === null || field === void 0 ? void 0 : field.params.allow_negative, true, false, userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale.value);
        const match = value ? regex.exec(value.toString()) : null;
        return !!match && match[0] !== "";
    }
    getInlineEditRules(field) {
        return {
            ...super.getInlineEditRules(field),
            editorWidth: "100%",
        };
    }
    canChangeValue(field, fromValue, toValue) {
        const { params: { allow_negative: allowNegative }, } = field;
        const isSignValid = this.isValueEmpty(toValue, field) ||
            !!allowNegative ||
            (!allowNegative && Number(toValue) >= 0);
        return isSignValid && super.canChangeValue(field, fromValue, toValue);
    }
    progressWidgetGoalValueToString(fieldValue, field, userPreferences) {
        const precision = (0, number_3.getNumberPrecision)(Number(fieldValue));
        const fieldWithCalculatedPrecision = {
            ...field,
            params: {
                ...field === null || field === void 0 ? void 0 : field.params,
                precision,
            },
        };
        return this.metricWidgetValueToString(fieldValue, fieldWithCalculatedPrecision, userPreferences);
    }
    getFillPattern(values, _field, reverse) {
        return (0, number_fill_pattern_1.getNumberFillPattern)(values, reverse);
    }
}
exports.BaseNumberFieldLogic = BaseNumberFieldLogic;
